// Live alerts (owner request 2026-10-17): when a new notification arrives, the bell pops it up on
// the page, and (if this browser allows it) as a desktop notification with an optional chime, so a
// new ticket or a safety report is noticed even with the tab in the background. Per browser:
// whether desktop alerts and the chime are on is kept in localStorage.

export interface AlertPrefs { desktop: boolean; sound: boolean; /** Kinds that only go in the bell, without a pop-up, desktop alert or sound. */ quiet?: string[] }

/** What a notification is about, for choosing which ones pop up (the school console's alert settings). */
export const ALERT_CATEGORIES: [string, string][] = [
  ["messages", "Messages"], ["safety", "Safety reports"], ["buses", "Buses"], ["closing", "Not collected by closing"],
  ["exceptions", "Flagged records"], ["requests", "Requests"], ["vavr", "Vavr support and announcements"], ["other", "Everything else"],
];
export function alertCategory(kind: string): string {
  if (kind === "message") return "messages";
  if (kind === "message_flagged" || kind.startsWith("safety")) return "safety";
  if (kind.startsWith("bus") || kind.startsWith("transport")) return "buses";
  if (kind.startsWith("closing")) return "closing";
  if (kind === "exception") return "exceptions";
  if (kind.startsWith("request") || kind.startsWith("pickup") || kind.startsWith("dropoff")) return "requests";
  if (kind === "support" || kind === "announcement" || kind === "test") return "vavr";
  return "other";
}
const key = (scope: string) => `vavr-alerts:${scope}`;

export function alertPrefs(scope: string): AlertPrefs {
  try { return { desktop: true, sound: true, ...JSON.parse(localStorage.getItem(key(scope)) ?? "{}") }; } catch { return { desktop: true, sound: true }; }
}
export function setAlertPrefs(scope: string, p: AlertPrefs) {
  try { localStorage.setItem(key(scope), JSON.stringify(p)); } catch { /* private window: the defaults stay */ }
}

export type DesktopState = "granted" | "denied" | "default" | "unsupported";
export const desktopState = (): DesktopState => (typeof Notification === "undefined" ? "unsupported" : Notification.permission);
export async function askDesktop(): Promise<DesktopState> {
  if (typeof Notification === "undefined") return "unsupported";
  try { return await Notification.requestPermission(); } catch { return Notification.permission; }
}

/** A desktop notification that brings the tab forward and opens `onClick` when clicked. */
export function showDesktop(title: string, body: string, onClick?: () => void, tag?: string) {
  if (desktopState() !== "granted") return;
  try {
    const n = new Notification(title, { body: body.slice(0, 200), tag, icon: "/favicon.svg" });
    n.onclick = () => { window.focus(); onClick?.(); n.close(); };
  } catch { /* some browsers only allow it from a service worker: the page pop-up still shows */ }
}

let ctx: AudioContext | null = null;
/** Two short soft tones: noticeable, not alarming. No sound file to load. */
export function chime() {
  try {
    ctx ??= new AudioContext();
    const now = ctx.currentTime;
    for (const [i, f] of [880, 1320].entries()) {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.value = f; o.type = "sine";
      g.gain.setValueAtTime(0, now + i * 0.15); g.gain.linearRampToValueAtTime(0.08, now + i * 0.15 + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.15 + 0.3);
      o.connect(g).connect(ctx.destination); o.start(now + i * 0.15); o.stop(now + i * 0.15 + 0.32);
    }
  } catch { /* no audio (or not yet allowed by the browser): silent */ }
}
